/**
 * Fiscal → Regras tributárias (matriz, nasce vazia): regras por grupo de tributo, versões com
 * vigência e o ciclo Rascunho → Testada → Aprovada (→ Vigente → Encerrada). Quem prepara só
 * aprova a própria versão com a permissão de autoaprovação (fiscal.matriz.aprovar_propria);
 * sem ela, outra pessoa aprova. Conflito entre regras (nenhuma inclui a outra) bloqueia a
 * aprovação.
 */
import {
  APPROVAL_SCOPE_LABEL,
  FISCAL_UFS,
  PRODUCT_ORIGINS,
  TAX_KINDS,
  TAX_KIND_LABEL,
  TAX_PARAM_FIELDS,
  domainLabel,
  type ReferenceRateView,
  type RuleConditions,
  type RuleSummary,
  type RuleVersionView,
  type TaxGroup,
  type TaxKind,
  type TestReport,
} from "@cafegestor/shared";
import { useState } from "react";
import { useCan } from "@/app/me";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { EmptyState } from "@/components/patterns";
import { apiDownload } from "@/lib/api";
import { SaveError } from "../cadastros/widgets";
import { ProductPicker } from "../estoque/pickers";
import {
  RULE_STATUS_LABEL,
  RULE_STATUS_TONE,
  useFiscalAction,
  useFiscalQuery,
  useOperations,
} from "./lib";
import { PendencyList } from "./PendenciasFiscaisPage";

const NO_COND: RuleConditions = {
  operationId: null,
  productId: null,
  taxGroupId: null,
  ncmPrefix: null,
  cest: null,
  origin: null,
  ufOrigin: null,
  ufDest: null,
  destScope: null,
  personKind: null,
  ieIndicator: null,
  finalConsumer: null,
  ruralProducer: null,
  model: null,
  purpose: null,
};

type Draft = {
  validFrom: string;
  validTo: string | null;
  conditions: RuleConditions;
  params: Record<string, string | boolean | string[]>;
  notes: string;
};

export function RegrasPage() {
  const can = useCan();
  const [kind, setKind] = useState<TaxKind | "">("");
  const [status, setStatus] = useState("todas");
  const [creating, setCreating] = useState(false);
  const params = new URLSearchParams({ status });
  if (kind) params.set("kind", kind);
  const rules = useFiscalQuery<RuleSummary[]>(`/regras?${params.toString()}`);
  const [importError, setImportError] = useState<unknown>(null);
  const importRules = useFiscalAction<{ rules: unknown[] }>(() => "/regras/importar");
  return (
    <div className="space-y-6">
      <PageTitle
        title="Regras tributárias"
        description="A matriz nasce vazia. Só regras APROVADAS e VIGENTES entram numa emissão; rascunhos servem para testar no simulador. Vence sempre a regra mais específica — regras que se cruzam sem que uma inclua a outra bloqueiam a emissão com a explicação."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() =>
                void apiDownload("/fiscal/regras/exportar?formato=csv", "matriz-tributaria.csv")
              }
            >
              Exportar CSV
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                void apiDownload("/fiscal/regras/exportar?formato=json", "matriz-tributaria.json")
              }
            >
              Exportar JSON
            </Button>
            {can("fiscal.matriz.preparar") && (
              <>
                <label className="inline-flex h-11 cursor-pointer items-center rounded-lg border border-input bg-card px-4 text-sm font-medium hover:bg-muted focus-within:outline-2 focus-within:outline-ring">
                  Importar JSON
                  <input
                    type="file"
                    accept="application/json"
                    className="hidden"
                    onChange={async (e) => {
                      setImportError(null);
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const json = JSON.parse(await file.text()) as { rules?: unknown[] };
                        importRules.mutate({
                          rules: (json.rules ?? [])
                            .map((r) => r as Record<string, unknown>)
                            .map((r) => ({ rule: r["rule"], version: r["version"] })),
                        });
                      } catch (err) {
                        setImportError(err);
                      }
                    }}
                  />
                </label>
                <Button onClick={() => setCreating(true)}>Nova regra</Button>
              </>
            )}
          </div>
        }
      />
      <SaveError error={importError ?? importRules.error} />
      {importRules.isSuccess && (
        <Alert tone="ok">Regras importadas como RASCUNHO (testar e aprovar antes de valer).</Alert>
      )}
      {creating && <NewRule onClose={() => setCreating(false)} />}
      <div className="flex flex-wrap gap-3">
        <Select
          aria-label="Grupo de tributos"
          className="w-56"
          value={kind}
          onChange={(e) => setKind(e.target.value as TaxKind | "")}
        >
          <option value="">Todos os grupos</option>
          {TAX_KINDS.map((k) => (
            <option key={k} value={k}>
              {TAX_KIND_LABEL[k]}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Situação da regra"
          className="w-56"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {(
            [
              "todas",
              "rascunho",
              "testada",
              "aprovada",
              "vigente",
              "encerrada",
              "arquivada",
            ] as const
          ).map((s) => (
            <option key={s} value={s}>
              {s === "todas" ? "Todas as situações" : RULE_STATUS_LABEL[s]}
            </option>
          ))}
        </Select>
      </div>
      {rules.isPending ? (
        <Spinner />
      ) : rules.data?.length === 0 ? (
        <EmptyState
          title="Nenhuma regra."
          description="Sem regra aprovada, a emissão fica bloqueada e o simulador mostra exatamente o que falta."
        />
      ) : (
        rules.data?.map((r) => <RuleCard key={r.id} rule={r} />)
      )}
      <ReferenceRates />
    </div>
  );
}

function ConditionsEditor({
  value,
  onChange,
}: {
  value: RuleConditions;
  onChange: (c: RuleConditions) => void;
}) {
  const ops = useOperations();
  const groups = useFiscalQuery<TaxGroup[]>("/grupos");
  const set = <K extends keyof RuleConditions>(k: K, v: RuleConditions[K]) =>
    onChange({ ...value, [k]: v });
  const sel = <K extends keyof RuleConditions>(
    k: K,
    label: string,
    options: [string, string][],
  ) => (
    <Field label={label}>
      <Select
        value={value[k] === null ? "" : String(value[k])}
        onChange={(e) => {
          const raw = e.target.value;
          const v = raw === "" ? null : raw === "true" ? true : raw === "false" ? false : raw;
          set(k, v as RuleConditions[K]);
        }}
      >
        <option value="">Qualquer</option>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </Select>
    </Field>
  );
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Condição vazia = vale para qualquer valor. Quanto mais condições, mais específica a regra.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {sel(
          "operationId",
          "Operação",
          (ops.data ?? []).map((o) => [o.id, `${o.code} — ${o.name}`]),
        )}
        <Field label="Produto">
          <ProductPicker
            value={value.productId ?? ""}
            label=""
            onChange={(id) => set("productId", id || null)}
          />
        </Field>
        {sel(
          "taxGroupId",
          "Grupo tributário",
          (groups.data ?? []).map((g) => [g.id, `${g.code} — ${g.name}`]),
        )}
        <Field label="NCM ou prefixo">
          <Input
            value={value.ncmPrefix ?? ""}
            onChange={(e) => set("ncmPrefix", e.target.value.replace(/\D/g, "") || null)}
          />
        </Field>
        <Field label="CEST">
          <Input
            value={value.cest ?? ""}
            onChange={(e) => set("cest", e.target.value.replace(/\D/g, "") || null)}
          />
        </Field>
        {sel(
          "origin",
          "Origem da mercadoria",
          Object.entries(PRODUCT_ORIGINS).map(([code, label]) => [code, label]),
        )}
        {sel(
          "ufOrigin",
          "UF de origem",
          FISCAL_UFS.map((u) => [u, u]),
        )}
        {sel(
          "ufDest",
          "UF de destino",
          FISCAL_UFS.map((u) => [u, u]),
        )}
        {sel("destScope", "Interna/interestadual", [
          ["interna", "Interna"],
          ["interestadual", "Interestadual"],
        ])}
        {sel("personKind", "PF/PJ", [
          ["pf", "PF"],
          ["pj", "PJ"],
        ])}
        {sel("ieIndicator", "Contribuinte", [
          ["1", "Contribuinte"],
          ["2", "Isento"],
          ["9", "Não contribuinte"],
        ])}
        {sel("finalConsumer", "Consumidor final", [
          ["true", "Sim"],
          ["false", "Não"],
        ])}
        {sel("ruralProducer", "Produtor rural", [
          ["true", "Sim"],
          ["false", "Não"],
        ])}
        {sel("model", "Modelo", [
          ["55", "NF-e (55)"],
          ["65", "NFC-e (65)"],
        ])}
        {sel("purpose", "Finalidade", [
          ["1", "Normal"],
          ["2", "Complementar"],
          ["3", "Ajuste"],
          ["4", "Devolução"],
        ])}
      </div>
    </div>
  );
}

export function ParamsEditor({
  kind,
  value,
  onChange,
}: {
  kind: TaxKind;
  value: Draft["params"];
  onChange: (p: Draft["params"]) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {TAX_PARAM_FIELDS[kind].map((p) => {
        if (p.type === "bool")
          return (
            <label key={p.key} className="flex items-center gap-2 pt-6 text-sm">
              <input
                type="checkbox"
                checked={value[p.key] === true}
                onChange={(e) => {
                  const next = { ...value };
                  if (e.target.checked) next[p.key] = true;
                  else delete next[p.key];
                  onChange(next);
                }}
              />
              {p.label}
            </label>
          );
        if (p.type === "list") {
          const list = Array.isArray(value[p.key]) ? (value[p.key] as string[]) : [];
          return (
            <Field key={p.key} label={p.label}>
              <div className="flex gap-3 pt-2 text-sm">
                {(p.listOptions ?? []).map((o) => (
                  <label key={o} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={list.includes(o)}
                      onChange={(e) => {
                        const nextList = e.target.checked
                          ? [...list, o]
                          : list.filter((x) => x !== o);
                        const next = { ...value };
                        if (nextList.length) next[p.key] = nextList;
                        else delete next[p.key];
                        onChange(next);
                      }}
                    />
                    {o}
                  </label>
                ))}
              </div>
            </Field>
          );
        }
        const current = typeof value[p.key] === "string" ? (value[p.key] as string) : "";
        // guarda o que foi digitado (inclusive espaços entre palavras); as pontas são aparadas
        // só ao salvar (`body`)
        const set = (v: string) => {
          const next = { ...value };
          if (v.trim()) next[p.key] = v;
          else delete next[p.key];
          onChange(next);
        };
        if (p.type === "select") {
          const options = p.options ?? [];
          // valor gravado antes que não está na lista: continua visível para não se perder
          const unknown = current !== "" && !options.some((o) => o.code === current);
          return (
            <Field key={p.key} label={p.label} {...(p.hint ? { hint: p.hint } : {})}>
              <Select value={current} onChange={(e) => set(e.target.value)}>
                <option value="">— não informado —</option>
                {unknown && <option value={current}>{current} (valor atual)</option>}
                {options.map((o) => (
                  <option key={o.code} value={o.code}>
                    {domainLabel(o)}
                  </option>
                ))}
              </Select>
            </Field>
          );
        }
        // texto: gravado exatamente como digitado; número: aceita vírgula (a API converte)
        return (
          <Field key={p.key} label={p.label} {...(p.hint ? { hint: p.hint } : {})}>
            <Input
              value={current}
              {...(p.numeric ? { inputMode: "decimal" as const } : {})}
              onChange={(e) => set(e.target.value)}
            />
          </Field>
        );
      })}
    </div>
  );
}

function VersionForm({
  kind,
  draft,
  setDraft,
}: {
  kind: TaxKind;
  draft: Draft;
  setDraft: (d: Draft) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Início da vigência">
          <Input
            type="date"
            value={draft.validFrom}
            onChange={(e) => setDraft({ ...draft, validFrom: e.target.value })}
          />
        </Field>
        <Field label="Fim (vazio = em aberto)">
          <Input
            type="date"
            value={draft.validTo ?? ""}
            onChange={(e) => setDraft({ ...draft, validTo: e.target.value || null })}
          />
        </Field>
        <Field label="Observação">
          <Input
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          />
        </Field>
      </div>
      <h4 className="font-semibold">Quando a regra vale</h4>
      <ConditionsEditor
        value={draft.conditions}
        onChange={(c) => setDraft({ ...draft, conditions: c })}
      />
      <h4 className="font-semibold">Parâmetros de {TAX_KIND_LABEL[kind]}</h4>
      <ParamsEditor
        kind={kind}
        value={draft.params}
        onChange={(p) => setDraft({ ...draft, params: p })}
      />
    </div>
  );
}

const emptyDraft = (): Draft => ({
  validFrom: new Date().toISOString().slice(0, 10),
  validTo: null,
  conditions: NO_COND,
  params: {},
  notes: "",
});
const draftOf = (v: RuleVersionView): Draft => ({
  validFrom: v.validFrom,
  validTo: v.validTo,
  conditions: v.conditions,
  params: Object.fromEntries(
    Object.entries(v.params).filter(([, x]) => x !== undefined),
  ) as Draft["params"],
  notes: v.notes ?? "",
});
const body = (d: Draft) => ({
  ...d,
  params: Object.fromEntries(
    Object.entries(d.params).map(([k, x]) => [k, typeof x === "string" ? x.trim() : x]),
  ),
  notes: d.notes.trim() || undefined,
});

function NewRule({ onClose }: { onClose: () => void }) {
  const [kind, setKind] = useState<TaxKind>("cfop");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const create = useFiscalAction<Record<string, unknown>>(
    () => "/regras",
    "POST",
    onClose,
    "Regra tributária criada como rascunho.",
  );
  return (
    <Card title="Nova regra (nasce como rascunho)">
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Grupo de tributo">
            <Select
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as TaxKind);
                setDraft({ ...draft, params: {} });
              }}
            >
              {TAX_KINDS.map((k) => (
                <option key={k} value={k}>
                  {TAX_KIND_LABEL[k]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Código">
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
          </Field>
          <Field label="Nome">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
        </div>
        <VersionForm kind={kind} draft={draft} setDraft={setDraft} />
        <div className="flex gap-2">
          <Button
            disabled={create.isPending}
            onClick={() => create.mutate({ rule: { kind, code, name }, version: body(draft) })}
          >
            Criar rascunho
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
        </div>
        <SaveError error={create.error} />
      </div>
    </Card>
  );
}

/** Quem preparou, testou e aprovou; a autoaprovação fica explícita. */
function VersionActors({ v }: { v: RuleVersionView }) {
  return (
    <span className="block space-y-0.5">
      <span className="block">Preparada: {v.preparedByEmail ?? v.createdByEmail ?? "—"}</span>
      {v.testedByEmail && <span className="block">Testada: {v.testedByEmail}</span>}
      {v.approvedByEmail && <span className="block">Aprovada: {v.approvedByEmail}</span>}
      {v.productionApprovedByEmail && v.productionApprovedByEmail !== v.approvedByEmail && (
        <span className="block">Liberada para produção: {v.productionApprovedByEmail}</span>
      )}
      {v.selfApproved && (
        <span className="block font-medium">
          Autoaprovada: a mesma pessoa preparou e aprovou (permissão de aprovar a própria regra)
        </span>
      )}
      {v.validationResponsible && (
        <span className="block">Validada por {v.validationResponsible}</span>
      )}
    </span>
  );
}

function RuleCard({ rule }: { rule: RuleSummary }) {
  const can = useCan();
  const [editing, setEditing] = useState<RuleVersionView | "nova" | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [report, setReport] = useState<TestReport | null>(null);
  const [closeFor, setCloseFor] = useState<RuleVersionView | null>(null);
  const [closeForm, setCloseForm] = useState({ validTo: "", reason: "" });
  const save = useFiscalAction<Record<string, unknown>>(
    () =>
      editing === "nova"
        ? `/regras/${rule.id}/versoes`
        : `/regras/versoes/${editing === null ? "" : editing.id}`,
    editing === "nova" ? "POST" : "PUT",
    () => setEditing(null),
    editing === "nova" ? "Nova versão criada como rascunho." : "Versão salva como rascunho.",
  );
  const test = useFiscalAction<{ id: string; version: number }, { report: TestReport }>(
    (b) => `/regras/versoes/${b.id}/testar`,
    "POST",
    (r) => setReport(r.report),
  );
  const approve = useFiscalAction<
    { id: string; version: number; scope: "homologacao" | "producao" },
    RuleSummary
  >(
    (b) => `/regras/versoes/${b.id}/aprovar`,
    "POST",
    undefined,
    (r) =>
      r.versions.some((x) => x.status === "aprovada" && x.approvalScope === "homologacao")
        ? "Versão aprovada somente para testes (simulador e homologação)."
        : "Versão aprovada para produção.",
  );
  const promote = useFiscalAction<{ id: string; version: number }>(
    (b) => `/regras/versoes/${b.id}/promover`,
    "POST",
    undefined,
    "Versão liberada para produção.",
  );
  const openPendencies = rule.pendencies.filter((p) => p.status === "aberta");
  const blockedReason =
    openPendencies.length > 0
      ? `Há ${openPendencies.length} pendência(s) fiscal(is) aberta(s): resolva antes de liberar para produção.`
      : undefined;
  const archive = useFiscalAction<{ id: string; version: number }>(
    (b) => `/regras/versoes/${b.id}/arquivar`,
    "POST",
    undefined,
    "Versão arquivada.",
  );
  const close = useFiscalAction<{ id: string; version: number; validTo: string; reason: string }>(
    (b) => `/regras/versoes/${b.id}/encerrar`,
    "POST",
    () => setCloseFor(null),
    "Vigência encerrada.",
  );
  const error =
    save.error ?? test.error ?? approve.error ?? promote.error ?? archive.error ?? close.error;
  return (
    <Card
      title={`${TAX_KIND_LABEL[rule.kind]} · ${rule.code} — ${rule.name}${rule.archived ? " (arquivada)" : ""}`}
      actions={
        can("fiscal.matriz.preparar") &&
        !rule.archived && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setDraft(rule.versions[0] ? draftOf(rule.versions[0]) : emptyDraft());
              setEditing("nova");
            }}
          >
            Nova versão
          </Button>
        )
      }
    >
      <Table
        head={["Versão", "Situação", "Vigência", "Condições", "Parâmetros", "Responsáveis", ""]}
      >
        {rule.versions.map((v) => (
          <tr key={v.id}>
            <td className="px-3 py-2">v{v.versionNo}</td>
            <td className="px-3 py-2">
              <Badge tone={RULE_STATUS_TONE[v.displayStatus]}>
                {RULE_STATUS_LABEL[v.displayStatus]}
              </Badge>
              {v.approvalScope && (
                <span className="mt-1 block">
                  <Badge tone={v.approvalScope === "producao" ? "ok" : "warn"}>
                    {APPROVAL_SCOPE_LABEL[v.approvalScope]}
                  </Badge>
                </span>
              )}
            </td>
            <td className="px-3 py-2 text-xs">
              {v.validFrom} a {v.validTo ?? "…"}
            </td>
            <td className="px-3 py-2 text-xs">
              {Object.entries(v.conditions)
                .filter(([, x]) => x !== null)
                .map(([k, x]) => `${k}: ${String(x)}`)
                .join("; ") || "qualquer"}
            </td>
            <td className="px-3 py-2 font-mono text-xs">{JSON.stringify(v.params)}</td>
            <td className="px-3 py-2 text-xs">
              <VersionActors v={v} />
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap">
              {(v.status === "rascunho" || v.status === "testada") &&
                can("fiscal.matriz.preparar") && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setDraft(draftOf(v));
                      setEditing(v);
                    }}
                  >
                    Editar
                  </Button>
                )}
              {v.status === "rascunho" && can("fiscal.matriz.preparar") && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => test.mutate({ id: v.id, version: v.version })}
                >
                  Testar
                </Button>
              )}
              {v.status === "testada" && can("fiscal.matriz.aprovar") && (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={approve.isPending}
                    onClick={() =>
                      approve.mutate({ id: v.id, version: v.version, scope: "homologacao" })
                    }
                  >
                    Aprovar para testes
                  </Button>
                  <Button
                    size="sm"
                    disabled={approve.isPending || blockedReason !== undefined}
                    title={blockedReason}
                    onClick={() =>
                      approve.mutate({ id: v.id, version: v.version, scope: "producao" })
                    }
                  >
                    Aprovar para produção
                  </Button>
                </>
              )}
              {v.status === "aprovada" &&
                v.approvalScope === "homologacao" &&
                v.displayStatus !== "encerrada" &&
                can("fiscal.matriz.aprovar") && (
                  <Button
                    size="sm"
                    disabled={promote.isPending || blockedReason !== undefined}
                    title={blockedReason}
                    onClick={() => promote.mutate({ id: v.id, version: v.version })}
                  >
                    Liberar para produção
                  </Button>
                )}
              {v.status === "aprovada" &&
                v.displayStatus !== "encerrada" &&
                can("fiscal.matriz.aprovar") && (
                  <Button size="sm" variant="ghost" onClick={() => setCloseFor(v)}>
                    Encerrar
                  </Button>
                )}
              {(v.status === "rascunho" || v.status === "testada") &&
                can("fiscal.matriz.preparar") && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => archive.mutate({ id: v.id, version: v.version })}
                  >
                    Arquivar
                  </Button>
                )}
            </td>
          </tr>
        ))}
      </Table>
      {rule.pendencies.length > 0 && (
        <div className="mt-3 space-y-2">
          <h3 className="text-sm font-semibold">
            Pendências fiscais
            {openPendencies.length > 0
              ? ` — ${openPendencies.length} aberta(s): aprovação só para testes até resolver`
              : " — todas resolvidas"}
          </h3>
          <PendencyList items={rule.pendencies} />
        </div>
      )}
      {closeFor && (
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-md border p-3">
          <Field label="Último dia de vigência">
            <Input
              type="date"
              value={closeForm.validTo}
              onChange={(e) => setCloseForm({ ...closeForm, validTo: e.target.value })}
            />
          </Field>
          <Field label="Motivo">
            <Input
              value={closeForm.reason}
              onChange={(e) => setCloseForm({ ...closeForm, reason: e.target.value })}
            />
          </Field>
          <Button
            onClick={() =>
              close.mutate({ id: closeFor.id, version: closeFor.version, ...closeForm })
            }
          >
            Encerrar vigência
          </Button>
          <Button variant="ghost" onClick={() => setCloseFor(null)}>
            Voltar
          </Button>
        </div>
      )}
      {report && (
        <div className="mt-3">
          <Alert tone={report.passed ? "ok" : "error"}>
            {!report.passed
              ? "Teste não passou:"
              : can("fiscal.matriz.aprovar") && can("fiscal.matriz.aprovar_propria")
                ? "Teste aprovado: a versão ficou TESTADA. Você pode aprová-la (tem a permissão de aprovar a própria regra) ou pedir a outra pessoa."
                : "Teste aprovado: a versão ficou TESTADA e aguarda a aprovação de outra pessoa."}
            <ul className="mt-1 list-disc pl-5">
              {report.completeness.map((c) => (
                <li key={c}>{c}</li>
              ))}
              {report.conflicts.map((c) => (
                <li key={c}>Conflito: {c}</li>
              ))}
              {report.cases.map((c) => (
                <li key={c.id}>
                  Caso "{c.name}": {c.passed ? "passou" : `falhou — ${c.differences.join(" ")}`}
                </li>
              ))}
            </ul>
          </Alert>
        </div>
      )}
      {editing && (
        <div className="mt-4 rounded-md border p-4">
          <VersionForm kind={rule.kind} draft={draft} setDraft={setDraft} />
          <div className="mt-3 flex gap-2">
            <Button
              disabled={save.isPending}
              onClick={() =>
                save.mutate(
                  editing === "nova" ? body(draft) : { ...body(draft), version: editing.version },
                )
              }
            >
              Salvar rascunho
            </Button>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
      <div className="mt-2">
        <SaveError error={error} />
      </div>
    </Card>
  );
}

function ReferenceRates() {
  const can = useCan();
  const rates = useFiscalQuery<ReferenceRateView[]>("/tabelas-referencia");
  const [f, setF] = useState({ code: "", tax: "cbs", rate: "", validFrom: "", validTo: "" });
  const create = useFiscalAction<Record<string, unknown>>(
    () => "/tabelas-referencia",
    "POST",
    undefined,
    "Valor de referência criado como rascunho.",
  );
  const approve = useFiscalAction<{ id: string; version: number }>(
    (b) => `/tabelas-referencia/${b.id}/aprovar`,
    "POST",
    undefined,
    "Valor de referência aprovado.",
  );
  return (
    <Card title="Tabelas de referência (ex.: alíquotas de IBS/CBS com vigência)">
      <p className="mb-2 text-xs text-muted-foreground">
        Sem valores pré-carregados. Quem cadastra só aprova o próprio valor com a permissão de
        aprovar a própria regra; sem ela, outra pessoa aprova.
      </p>
      <Table head={["Código", "Tributo", "Alíquota %", "Vigência", "Situação", ""]}>
        {rates.data?.map((r) => (
          <tr key={r.id}>
            <td className="px-3 py-2 font-mono">{r.code}</td>
            <td className="px-3 py-2">{r.tax}</td>
            <td className="text-right tabular-nums px-3 py-2">{r.rate}</td>
            <td className="px-3 py-2 text-xs">
              {r.validFrom} a {r.validTo ?? "…"}
            </td>
            <td className="px-3 py-2">
              {r.status}
              {r.selfApproved && (
                <span className="block text-xs text-muted-foreground">
                  autoaprovada por {r.approvedByEmail}
                </span>
              )}
            </td>
            <td className="px-3 py-2 text-right">
              {r.status === "rascunho" && can("fiscal.matriz.aprovar") && (
                <Button size="sm" onClick={() => approve.mutate({ id: r.id, version: r.version })}>
                  Aprovar
                </Button>
              )}
            </td>
          </tr>
        ))}
      </Table>
      {can("fiscal.matriz.preparar") && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <Field label="Código">
            <Input
              value={f.code}
              onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
            />
          </Field>
          <Field label="Tributo">
            <Select value={f.tax} onChange={(e) => setF({ ...f, tax: e.target.value })}>
              {["ibs_uf", "ibs_mun", "cbs", "is", "outro"].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Alíquota %">
            <Input
              value={f.rate}
              inputMode="decimal"
              onChange={(e) => setF({ ...f, rate: e.target.value })}
            />
          </Field>
          <Field label="Início">
            <Input
              type="date"
              value={f.validFrom}
              onChange={(e) => setF({ ...f, validFrom: e.target.value })}
            />
          </Field>
          <Field label="Fim">
            <Input
              type="date"
              value={f.validTo}
              onChange={(e) => setF({ ...f, validTo: e.target.value })}
            />
          </Field>
          <Button
            onClick={() => create.mutate({ ...f, validTo: f.validTo || null, description: null })}
          >
            Adicionar
          </Button>
        </div>
      )}
      <SaveError error={create.error ?? approve.error} />
    </Card>
  );
}
